import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import FilterBar, {
  QuickFilterConfig,
  ActiveFilter,
  FilterBarProps,
} from '@/components/ui/FilterBar';
import { FilterChips } from '@/components/ui/FilterChips';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PipelineFilterUser {
  id: string;
  name: string;
  role: string;
  email: string;
}

export interface PipelineFilterBarProps {
  /** Callback fired whenever the derived API query params change */
  onFilterParamsChange: (params: Record<string, any>) => void;
  /** Users list for the Owner quick-filter */
  users?: PipelineFilterUser[];
  /** Optional className for the wrapper */
  className?: string;
}

/** The shape we emit via onFilterParamsChange — mirrors the leads API */
export interface PipelineApiParams {
  query?: string;
  stage?: string;           // comma-separated
  status?: string;          // comma-separated (priorities)
  assignedTo?: string;      // comma-separated user IDs
  sourceId?: string;
  minValue?: number;
  maxValue?: number;
  createdAfter?: string;    // ISO date
  createdBefore?: string;   // ISO date
  closureDateFrom?: string; // ISO date
  closureDateTo?: string;   // ISO date
  lastContactFrom?: string; // ISO date
  lastContactTo?: string;   // ISO date
  riskLevel?: string;
}

// ---------------------------------------------------------------------------
// Constants — Quick Filter Configurations
// ---------------------------------------------------------------------------

const STAGE_OPTIONS = [
  { value: 'NEW', label: 'New', color: '#3B82F6' },
  { value: 'CONTACTED', label: 'Contacted', color: '#8B5CF6' },
  { value: 'DISCUSSION', label: 'Discussion', color: '#F59E0B' },
  { value: 'PROPOSAL', label: 'Proposal', color: '#10B981' },
  { value: 'NEGOTIATION', label: 'Negotiation', color: '#F97316' },
  { value: 'WON', label: 'Won', color: '#22C55E' },
  { value: 'LOST', label: 'Lost', color: '#EF4444' },
];

const PRIORITY_OPTIONS = [
  { value: 'HOT', label: 'Hot', color: '#EF4444' },
  { value: 'WARM', label: 'Warm', color: '#F59E0B' },
  { value: 'COLD', label: 'Cold', color: '#3B82F6' },
];

const RISK_LEVEL_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

const INDUSTRY_OPTIONS = [
  { value: 'FITNESS', label: 'Fitness' },
  { value: 'HEALTHCARE', label: 'Healthcare' },
  { value: 'TECHNOLOGY', label: 'Technology' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'HOSPITALITY', label: 'Hospitality' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'OTHER', label: 'Other' },
];

const COMPANY_SIZE_OPTIONS = [
  { value: 'SMALL', label: 'Small (1-50)' },
  { value: 'MEDIUM', label: 'Medium (51-200)' },
  { value: 'LARGE', label: 'Large (201-500)' },
  { value: 'ENTERPRISE', label: 'Enterprise (500+)' },
];

// ---------------------------------------------------------------------------
// Advanced Filter Field Definitions
// ---------------------------------------------------------------------------

export interface AdvancedFieldConfig {
  key: string;
  label: string;
  type: 'number' | 'select' | 'date' | 'text';
  options?: { value: string; label: string }[];
}

export const LEAD_ADVANCED_FIELDS: AdvancedFieldConfig[] = [
  { key: 'dealValue', label: 'Deal Value', type: 'number' },
  { key: 'riskLevel', label: 'Risk Level', type: 'select', options: RISK_LEVEL_OPTIONS },
  { key: 'industry', label: 'Industry', type: 'select', options: INDUSTRY_OPTIONS },
  { key: 'companySize', label: 'Company Size', type: 'select', options: COMPANY_SIZE_OPTIONS },
  { key: 'createdAt', label: 'Created Date', type: 'date' },
  { key: 'lastContactDate', label: 'Last Contact', type: 'date' },
  { key: 'followUpDate', label: 'Follow-up Date', type: 'date' },
];

// ---------------------------------------------------------------------------
// Helper: Build quick-filter configs (users list is dynamic)
// ---------------------------------------------------------------------------

function buildQuickFilters(users?: PipelineFilterUser[]): QuickFilterConfig[] {
  const ownerOptions = (users || []).map((u) => ({
    value: u.id,
    label: u.name,
  }));

  return [
    {
      key: 'stages',
      label: 'Stage',
      type: 'multi-select' as const,
      options: STAGE_OPTIONS,
    },
    {
      key: 'priorities',
      label: 'Priority',
      type: 'multi-select' as const,
      options: PRIORITY_OPTIONS,
    },
    {
      key: 'assignedTo',
      label: 'Owner',
      type: 'user-select' as const,
      options: ownerOptions,
    },
    {
      key: 'expectedClose',
      label: 'Close Date',
      type: 'date-range' as const,
    },
  ];
}

// ---------------------------------------------------------------------------
// Helper: Convert ActiveFilter[] + search term into API query params
// ---------------------------------------------------------------------------

function filtersToApiParams(
  activeFilters: ActiveFilter[],
  searchTerm: string
): PipelineApiParams {
  const params: PipelineApiParams = {};

  if (searchTerm) {
    params.query = searchTerm;
  }

  // Group multi-select values by key
  const multiValues: Record<string, string[]> = {};
  // Single-value / range filters
  const rangeValues: Record<string, any> = {};

  for (const filter of activeFilters) {
    // Date-range filters store { from, to } as value
    if (
      filter.value &&
      typeof filter.value === 'object' &&
      ('from' in filter.value || 'to' in filter.value)
    ) {
      rangeValues[filter.key] = filter.value;
      continue;
    }

    // Number-range filters store { min, max } as value
    if (
      filter.value &&
      typeof filter.value === 'object' &&
      ('min' in filter.value || 'max' in filter.value)
    ) {
      rangeValues[filter.key] = filter.value;
      continue;
    }

    // Multi-select: each selected option is a separate ActiveFilter
    if (typeof filter.value === 'string') {
      if (!multiValues[filter.key]) multiValues[filter.key] = [];
      multiValues[filter.key].push(filter.value);
    }
  }

  // --- Multi-select → comma-separated strings ---
  if (multiValues.stages?.length) {
    params.stage = multiValues.stages.join(',');
  }
  if (multiValues.priorities?.length) {
    params.status = multiValues.priorities.join(',');
  }
  if (multiValues.assignedTo?.length) {
    params.assignedTo = multiValues.assignedTo.join(',');
  }
  if (multiValues.sourceId?.length) {
    params.sourceId = multiValues.sourceId.join(',');
  }

  // --- Risk level (from advanced) ---
  if (multiValues.riskLevel?.length) {
    params.riskLevel = multiValues.riskLevel[0]; // single-select
  }

  // --- Date ranges ---
  const expectedClose = rangeValues.expectedClose;
  if (expectedClose) {
    if (expectedClose.from) params.closureDateFrom = expectedClose.from;
    if (expectedClose.to) params.closureDateTo = expectedClose.to;
  }

  const createdAt = rangeValues.createdAt;
  if (createdAt) {
    if (createdAt.from) params.createdAfter = createdAt.from;
    if (createdAt.to) params.createdBefore = createdAt.to;
  }

  const lastContactDate = rangeValues.lastContactDate;
  if (lastContactDate) {
    if (lastContactDate.from) params.lastContactFrom = lastContactDate.from;
    if (lastContactDate.to) params.lastContactTo = lastContactDate.to;
  }

  const followUpDate = rangeValues.followUpDate;
  if (followUpDate) {
    if (followUpDate.from) params.closureDateFrom = followUpDate.from;
    if (followUpDate.to) params.closureDateTo = followUpDate.to;
  }

  // --- Number ranges ---
  const dealValue = rangeValues.dealValue;
  if (dealValue) {
    if (dealValue.min !== undefined) params.minValue = dealValue.min;
    if (dealValue.max !== undefined) params.maxValue = dealValue.max;
  }

  return params;
}

// ---------------------------------------------------------------------------
// Helper: Convert ActiveFilter[] → condensed FilterChips format
//   FilterChips expects one entry per filter *group* (e.g. "Stage: New, Contacted")
//   but ActiveFilter[] has one entry per individual value. We merge them.
// ---------------------------------------------------------------------------

interface CondensedChip {
  key: string;
  label: string;
  displayValue: string;
  value: any;
}

function condenseFilters(filters: ActiveFilter[]): CondensedChip[] {
  const groups: Record<string, { label: string; displays: string[]; value: any }> = {};

  for (const f of filters) {
    if (!groups[f.key]) {
      groups[f.key] = { label: f.label, displays: [], value: f.value };
    }
    groups[f.key].displays.push(f.displayValue);
  }

  return Object.entries(groups).map(([key, g]) => ({
    key,
    label: g.label,
    displayValue: g.displays.join(', '),
    value: g.value,
  }));
}

// ---------------------------------------------------------------------------
// PipelineFilterBar Component
// ---------------------------------------------------------------------------

export const PipelineFilterBar: React.FC<PipelineFilterBarProps> = ({
  onFilterParamsChange,
  users,
  className,
}) => {
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Build quick filter configs (re-compute when users list changes)
  const quickFilters = useMemo(() => buildQuickFilters(users), [users]);

  // Compute and emit API params whenever filters or search change
  const apiParams = useMemo(
    () => filtersToApiParams(activeFilters, searchTerm),
    [activeFilters, searchTerm]
  );

  const prevParamsRef = useRef<string>('');
  useEffect(() => {
    const serialized = JSON.stringify(apiParams);
    if (serialized !== prevParamsRef.current) {
      prevParamsRef.current = serialized;
      onFilterParamsChange(apiParams);
    }
  }, [apiParams, onFilterParamsChange]);

  // --- Callbacks ---

  const handleFilterChange = useCallback((filters: ActiveFilter[]) => {
    setActiveFilters(filters);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleMoreFiltersClick = useCallback(() => {
    setShowAdvanced((prev) => !prev);
  }, []);

  /** Remove all filters that share the given key */
  const handleRemoveFilterGroup = useCallback(
    (filterKey: string) => {
      setActiveFilters((prev) => prev.filter((f) => f.key !== filterKey));
    },
    []
  );

  const handleClearAll = useCallback(() => {
    setActiveFilters([]);
    setSearchTerm('');
  }, []);

  // Condensed chips for the secondary row
  const condensedChips = useMemo(() => condenseFilters(activeFilters), [activeFilters]);

  // --- Advanced filter handlers ---

  const handleAdvancedSelect = useCallback(
    (fieldKey: string, fieldLabel: string, value: string, displayLabel: string) => {
      setActiveFilters((prev) => {
        // Replace any existing filter for this key
        const without = prev.filter((f) => f.key !== fieldKey);
        return [...without, { key: fieldKey, label: fieldLabel, value, displayValue: displayLabel }];
      });
    },
    []
  );

  const handleAdvancedDateRange = useCallback(
    (fieldKey: string, fieldLabel: string, from: string, to: string) => {
      setActiveFilters((prev) => {
        const without = prev.filter((f) => f.key !== fieldKey);
        const display = `${from || '...'} - ${to || '...'}`;
        return [
          ...without,
          { key: fieldKey, label: fieldLabel, value: { from, to }, displayValue: display },
        ];
      });
    },
    []
  );

  const handleAdvancedNumberRange = useCallback(
    (fieldKey: string, fieldLabel: string, min?: number, max?: number) => {
      setActiveFilters((prev) => {
        const without = prev.filter((f) => f.key !== fieldKey);
        const display = `${min ?? 0} - ${max ?? 'Any'}`;
        return [
          ...without,
          { key: fieldKey, label: fieldLabel, value: { min, max }, displayValue: display },
        ];
      });
    },
    []
  );

  return (
    <div className={cn('space-y-0', className)}>
      {/* Primary filter bar with quick filters + search */}
      <FilterBar
        quickFilters={quickFilters}
        searchPlaceholder="Search leads by name, company, phone..."
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onMoreFiltersClick={handleMoreFiltersClick}
        searchTerm={searchTerm}
      />

      {/* Condensed filter chips row */}
      <FilterChips
        filters={condensedChips}
        onRemove={handleRemoveFilterGroup}
        onClearAll={handleClearAll}
      />

      {/* Inline advanced filter panel (slides down) */}
      {showAdvanced && (
        <div className="border border-gray-200 rounded-lg bg-white p-4 mt-2 space-y-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Advanced Filters</h3>
            <button
              type="button"
              onClick={() => setShowAdvanced(false)}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Close
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LEAD_ADVANCED_FIELDS.map((field) => (
              <AdvancedFieldInput
                key={field.key}
                field={field}
                activeFilters={activeFilters}
                onSelectValue={handleAdvancedSelect}
                onDateRange={handleAdvancedDateRange}
                onNumberRange={handleAdvancedNumberRange}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AdvancedFieldInput — renders one field in the advanced panel
// ---------------------------------------------------------------------------

const AdvancedFieldInput: React.FC<{
  field: AdvancedFieldConfig;
  activeFilters: ActiveFilter[];
  onSelectValue: (key: string, label: string, value: string, displayLabel: string) => void;
  onDateRange: (key: string, label: string, from: string, to: string) => void;
  onNumberRange: (key: string, label: string, min?: number, max?: number) => void;
}> = ({ field, activeFilters, onSelectValue, onDateRange, onNumberRange }) => {
  // Find current value for this field from active filters
  const current = activeFilters.find((f) => f.key === field.key);

  if (field.type === 'select' && field.options) {
    const currentVal = typeof current?.value === 'string' ? current.value : '';
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
        <select
          value={currentVal}
          onChange={(e) => {
            const opt = field.options!.find((o) => o.value === e.target.value);
            if (opt) {
              onSelectValue(field.key, field.label, opt.value, opt.label);
            }
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All</option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'number') {
    const range = current?.value as { min?: number; max?: number } | undefined;
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Min"
            defaultValue={range?.min ?? ''}
            onBlur={(e) => {
              const min = e.target.value ? parseFloat(e.target.value) : undefined;
              onNumberRange(field.key, field.label, min, range?.max);
            }}
            className="flex-1 rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <span className="text-gray-400 text-xs">to</span>
          <input
            type="number"
            placeholder="Max"
            defaultValue={range?.max ?? ''}
            onBlur={(e) => {
              const max = e.target.value ? parseFloat(e.target.value) : undefined;
              onNumberRange(field.key, field.label, range?.min, max);
            }}
            className="flex-1 rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
    );
  }

  if (field.type === 'date') {
    const dateRange = current?.value as { from?: string; to?: string } | undefined;
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            defaultValue={dateRange?.from ?? ''}
            onBlur={(e) => {
              onDateRange(field.key, field.label, e.target.value, dateRange?.to ?? '');
            }}
            className="flex-1 rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <span className="text-gray-400 text-xs">to</span>
          <input
            type="date"
            defaultValue={dateRange?.to ?? ''}
            onBlur={(e) => {
              onDateRange(field.key, field.label, dateRange?.from ?? '', e.target.value);
            }}
            className="flex-1 rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
    );
  }

  // Fallback: text input
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
      <input
        type="text"
        placeholder={`Filter by ${field.label.toLowerCase()}`}
        defaultValue={typeof current?.value === 'string' ? current.value : ''}
        onBlur={(e) => {
          if (e.target.value) {
            onSelectValue(field.key, field.label, e.target.value, e.target.value);
          }
        }}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </div>
  );
};

export default PipelineFilterBar;
